import React, { useRef, useState } from "react";
import { KeyRound, Loader2, UserCog, Upload, Image as ImageIcon, Ban } from "lucide-react";
import { useConsejoExcluidos } from "../lib/consejoExcluidos";
import { useLogo, reloadLogo } from "../lib/logo";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";

function Field({ label, value, onChange, testid, autoComplete }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">{label}</span>
      <input type="password" className="input mt-1 w-full" value={value} onChange={(e) => onChange(e.target.value)}
             autoComplete={autoComplete} data-testid={testid} />
    </label>
  );
}

function ChangePassword({ hasPassword, onChanged }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [rep, setRep] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (nw.length < 8) return setErr("La nueva contraseña debe tener al menos 8 caracteres");
    if (nw !== rep) return setErr("Las dos contraseñas nuevas no coinciden");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success(hasPassword ? "Contraseña cambiada" : "Contraseña creada");
      onChanged?.();
      setCur(""); setNw(""); setRep("");
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || "No se pudo cambiar la contraseña");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card p-6 max-w-md space-y-4" data-testid="change-password-form">
      <div className="flex items-center gap-2"><KeyRound size={16} /><h2 className="font-display text-lg font-bold">{hasPassword ? "Cambiar contraseña" : "Crear contraseña"}</h2></div>
      {hasPassword ? <Field label="Contraseña actual" value={cur} onChange={setCur} testid="current-password-input" autoComplete="current-password" />
        : <p className="text-xs text-slate-500">Entras con Google. Si quieres, crea también una contraseña para entrar con tu email.</p>}
      <Field label="Nueva contraseña (mín. 8 caracteres)" value={nw} onChange={setNw} testid="new-password-input" autoComplete="new-password" />
      <Field label="Repite la nueva contraseña" value={rep} onChange={setRep} testid="repeat-password-input" autoComplete="new-password" />
      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="change-password-error">{err}</div>}
      <button type="submit" disabled={busy || (hasPassword && !cur) || !nw || !rep} className="btn btn-primary" data-testid="change-password-submit">
        {busy && <Loader2 size={14} className="animate-spin" />} Guardar nueva contraseña
      </button>
    </form>
  );
}

function LogoUpload() {
  const logo = useLogo();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("El logo no puede pasar de 2 MB");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await reloadLogo();
      toast.success("Logo guardado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo subir el logo");
    } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const quitar = async () => {
    await api.delete("/branding/logo");
    await reloadLogo();
    toast.success("Logo quitado");
  };
  return (
    <div className="card p-6 max-w-md mb-6" data-testid="logo-card">
      <div className="flex items-center gap-2 mb-1"><ImageIcon size={16} /><h2 className="font-display text-lg font-bold">Logo de la farmacia</h2></div>
      <p className="text-xs text-slate-500 mb-3">Aparece en la cabecera de la aplicación y en todos los PDF. PNG, JPG o WEBP, máximo 2 MB.</p>
      <div className="h-20 rounded-lg border border-dashed border-slate-300 flex items-center justify-center bg-slate-50 mb-3" data-testid="logo-preview">
        {logo.url ? <img src={logo.url} alt="Logo" className="max-h-16 max-w-[220px] object-contain" /> : <span className="text-xs text-slate-400">Sin logo</span>}
      </div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="logo-file-input" />
      <div className="flex gap-2">
        <button onClick={() => ref.current?.click()} disabled={busy} className="btn btn-primary" data-testid="logo-upload-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {logo.url ? "Cambiar logo" : "Subir logo"}
        </button>
        {logo.url && <button onClick={quitar} className="btn btn-outline" data-testid="logo-remove-btn">Quitar</button>}
      </div>
    </div>
  );
}

function ConsejoExcluidos() {
  const excl = useConsejoExcluidos();
  const [cn, setCn] = useState("");
  const add = async (e) => {
    e.preventDefault();
    if (!cn.trim()) return;
    await excl.toggle(cn.trim(), null);
    setCn("");
  };
  return (
    <div className="card p-6 max-w-md mb-6" data-testid="consejo-excluidos-card">
      <div className="flex items-center gap-2 mb-1"><Ban size={16} /><h2 className="font-display text-lg font-bold">Códigos que no son consejo</h2></div>
      <p className="text-xs text-slate-500 mb-3">
        Servicios o productos (como el SPD) que no deben contar como consejo ni salir en los rankings. Al excluir o readmitir un código se
        recalculan todos los meses ya subidos (tarda unos segundos).
      </p>
      <form onSubmit={add} className="flex gap-2 mb-3">
        <input className="input flex-1" placeholder="Código nacional (CN)" value={cn} onChange={(e) => setCn(e.target.value)} data-testid="consejo-excluido-input" />
        <button className="btn btn-primary" data-testid="consejo-excluido-add-btn">Añadir</button>
      </form>
      {excl.items.length === 0 ? <p className="text-xs text-slate-400" data-testid="consejo-excluidos-vacio">No hay códigos excluidos.</p> : (
        <ul className="divide-y divide-slate-100">
          {excl.items.map((i) => (
            <li key={i.cn} className="flex items-center justify-between gap-2 py-2 text-sm" data-testid={`consejo-excluido-${i.cn}`}>
              <div className="min-w-0"><span className="font-mono text-xs text-slate-500 mr-2">{i.cn}</span><span className="truncate">{i.descripcion || ""}</span></div>
              <button onClick={() => excl.toggle(i.cn)} className="text-xs font-semibold text-red-600 hover:underline" data-testid={`consejo-excluido-quitar-${i.cn}`}>Quitar</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Perfil() {
  const { user, setUser } = useAuth();
  return (
    <div className="p-8 fade-in" data-testid="perfil-page">
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Cuenta</div>
      <h1 className="font-display text-4xl font-extrabold mt-1 tracking-tight flex items-center gap-3 mb-6"><UserCog size={30} /> Mi perfil</h1>
      <div className="card p-6 max-w-md mb-6 text-sm space-y-1" data-testid="perfil-datos">
        <div><span className="text-slate-500">Nombre:</span> <b>{user?.name}</b></div>
        <div><span className="text-slate-500">Email:</span> <b>{user?.email}</b></div>
        {user?.username && <div><span className="text-slate-500">Usuario:</span> <b>{user.username}</b></div>}
        {user?.pharmacy_name && <div><span className="text-slate-500">Farmacia:</span> <b>{user.pharmacy_name}</b></div>}
      </div>
      <LogoUpload />
      <ConsejoExcluidos />
      <ChangePassword hasPassword={user?.has_password !== false} onChanged={() => setUser({ ...user, has_password: true })} />
    </div>
  );
}
